import { useEffect, useMemo, useRef, useState } from "react";
// MapLibre, not Mapbox (de-Mapbox phase 1) — same v1 API surface, so the
// local `mapboxgl` name stays to keep this file's diff minimal.
// Default import + v5 pinned: v5 ships a default export everywhere (v6
// flipped to named-only and broke the deploy — and rendering in WKWebView).
import mapboxgl from "maplibre-gl";
import { PLEASANT_LIGHT_STYLE } from "@/lib/pleasantMapStyle";
import { MAPBOX_TOKEN } from "@/lib/env";
import type { ClassifiedRoute } from "@/utils/scoring";
import { geometryBuckets, jaccard, selfOverlapFraction } from "@/utils/scoring";
import type { CommunityTag } from "@/api/community";
import { fetchBikeLanesAlongRoute, type BikeLaneSegment } from "@/api/bikeLanes";
import type { GooglePlaceHit } from "@/api/googlePlaces";
import type { PhotoPin } from "@/api/photoPins";
import type { TipPin } from "@/api/tips";
import type { LngLat } from "@/utils/polyline";
import { bbox, nearestPointOnPolyline } from "@/utils/polyline";
import { subscribeMapHighlight, type MapHighlightPayload } from "@/utils/mapHighlight";
import { niceTownsAlongRoute } from "@/utils/niceTowns";
import { hardenMapAgainstTeardown } from "@/utils/mapTeardown";
import { navPerf } from "@/utils/navPerf";
import { fmtDistance } from "@/utils/units";

/**
 * Is this map still usable?
 *
 * Sentry, Aug 1: `TypeError: undefined is not an object (evaluating
 * 'this.style.getOwnLayer')` — thrown from inside map.getLayer(). The
 * breadcrumbs read: click → navigate to /about → crash 60ms later.
 *
 * When MapLibre tears a map down it sets `style` to undefined, but effect
 * cleanups, pending `styledata` callbacks and in-flight fetches can still fire
 * afterwards and reach for a layer. getLayer() then dereferences a style that
 * isn't there and takes the whole page down through the error boundary — so
 * leaving the map screen at the wrong moment white-screened the site.
 *
 * One guard, used at every teardown site. `_removed` is MapLibre-internal but
 * it's the only honest signal that remove() has been called.
 */
function mapAlive(map: mapboxgl.Map | null | undefined): boolean {
  if (!map) return false;
  const m = map as unknown as { style?: unknown; _removed?: boolean };
  return !!m.style && !m._removed;
}

interface MapViewProps {
  routes?: ClassifiedRoute[];
  selectedClassification?: "fastest" | "scenic" | "pleasant";
  start?: LngLat;
  end?: LngLat;
  userLocation?: LngLat;
  communityTags?: CommunityTag[];
  showCommunity?: boolean;
  onMapClick?: (lngLat: LngLat) => void;
  /**
   * Optional floating "Save this place" / saved-label pill rendered just
   * above the destination pin. Tap → open the save/edit dialog in the host.
   */
  destinationSaveLabel?: string;
  onDestinationSaveClick?: () => void;
  /** When set, fly to that community tag and open its popup. */
  focusedTagId?: string | null;
  /**
   * Travel mode — drives the camera style when heading-up is enabled:
   *   driving → 3D tilted (~55° pitch), follows user
   *   walking/biking → flat top-down, just rotated to heading
   */
  travelMode?: "walking" | "biking" | "driving";
  /** Live compass/GPS heading in degrees (0 = north). */
  heading?: number | null;
  /** When true, rotate the map to match `heading`. */
  headingUp?: boolean;
  /** Toggle handler for the compass button. */
  onToggleHeadingUp?: () => void;
  /** Photo pins (own + public) to render as thumbnail markers. */
  photoPins?: PhotoPin[];
  /** Tap handler for a photo pin marker. */
  onPhotoPinClick?: (pin: PhotoPin) => void;
  /** Camera button handler — drops a pin at current location. */
  onAddPhotoHere?: () => void;
  /** Long-press on map handler — drops a pin at the long-pressed point. */
  onLongPress?: (lngLat: LngLat) => void;
  /** Draggable "position the star" marker (Morgan: after tapping the map
   *  to leave a tip / pin a photo, "you should see a star come up and be
   *  able to move it"). Non-null = render a draggable star at the coords. */
  adjustablePin?: LngLat | null;
  /** Fired on dragend with the star's new position. */
  onAdjustablePinMove?: (lngLat: LngLat) => void;
  /** Tap on a route polyline → select that classification. */
  onSelectClassification?: (cls: "fastest" | "scenic" | "pleasant") => void;
  /** Standalone tip pins to render. */
  tipPins?: TipPin[];
  /** Tap handler for a tip pin. */
  onTipPinClick?: (tip: TipPin) => void;
  /** Intermediate stops to render as star markers along the route. */
  stops?: { coords: LngLat; name?: string }[];
  /** Pleasant detour waypoints the routing applied automatically. Rendered
   *  as sparkle markers with a "why we routed here" popup. */
  detourMarkers?: { coords: LngLat; name?: string; reason?: string }[];
  /** Google Maps reference polyline coordinates (for visual comparison). */
  googleRouteCoords?: LngLat[] | null;
  /** Google Maps duration + distance — when present alongside the polyline,
   *  surfaces as a small stats label next to the toggle so the user can
   *  compare numbers directly even when the two polylines look similar at
   *  zoom. Real-drive note (#73). */
  googleDurationS?: number | null;
  googleDistanceM?: number | null;
  /** Selected route's duration + distance + polyline. When supplied, the
   *  diagnostic dialog (#74) computes a Jaccard overlap between this
   *  polyline and Google's so we can answer "are the lines really the
   *  same path?" without rebuilding the app. */
  selectedRouteDurationS?: number | null;
  selectedRouteDistanceM?: number | null;
  selectedRouteCoords?: LngLat[] | null;
  selectedRouteLabel?: string;
  /** When true, render the Google reference polyline. */
  showGoogleRoute?: boolean;
  /** Toggle handler for the Google polyline visibility button. */
  onToggleGoogleRoute?: () => void;
  className?: string;
}


const COLORS: Record<string, string> = {
  // (#150) Direct/fastest was rendering as light grey #8a8e8a at width 3
  // and opacity 0.55 — invisible on Mapbox's light street basemap, so
  // even when the Direct route's coordinates were correct (Bedford Rd
  // 4.6mi) the line couldn't be seen under the bold Pleasant polyline.
  // Darker slate is high-contrast on the map yet still reads as "the
  // utility / no-frills option" (versus the teal Pleasant and green
  // Scenic which carry semantic warmth).
  fastest: "#3b4252",
  scenic: "#5fb87a",
  pleasant: "#1f8a85",
};

/** Map our internal place-type bucket to a pin color + emoji glyph. */
const PLACE_PIN_STYLE: Partial<Record<GooglePlaceHit["type"], { bg: string; glyph: string; label: string }>> = {
  cafe: { bg: "#a8722d", glyph: "☕", label: "Café" },
  shop: { bg: "#7a4ec9", glyph: "🛍", label: "Shop" },
};

export const MapView = ({
  routes = [],
  selectedClassification = "pleasant",
  start,
  end,
  userLocation,
  communityTags = [],
  showCommunity = false,
  onMapClick,
  destinationSaveLabel,
  onDestinationSaveClick,
  focusedTagId,
  travelMode = "walking",
  heading = null,
  headingUp = false,
  onToggleHeadingUp,
  photoPins = [],
  onPhotoPinClick,
  onAddPhotoHere,
  onLongPress,
  adjustablePin = null,
  onAdjustablePinMove,
  onSelectClassification,
  tipPins = [],
  onTipPinClick,
  stops = [],
  detourMarkers = [],
  googleRouteCoords = null,
  googleDurationS = null,
  googleDistanceM = null,
  selectedRouteDurationS = null,
  selectedRouteDistanceM = null,
  selectedRouteCoords = null,
  selectedRouteLabel,
  showGoogleRoute = false,
  onToggleGoogleRoute,
  className,
}: MapViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  /** Trip pins (start / destination / stops / pleasant detours) keyed by
   *  everything that can change about them, so a pin that hasn't moved is
   *  never torn down and rebuilt. See the reconcile block in the route-draw
   *  effect for why identity matters here. */
  const tripPinsRef = useRef<Map<string, mapboxgl.Marker>>(new globalThis.Map());
  const tagMarkersRef = useRef<Map<string, mapboxgl.Marker>>(new globalThis.Map());
  const tagPopupsRef = useRef<Map<string, mapboxgl.Popup>>(new globalThis.Map());
  const placeMarkersRef = useRef<Map<string, mapboxgl.Marker>>(new globalThis.Map());
  const photoMarkersRef = useRef<Map<string, mapboxgl.Marker>>(new globalThis.Map());
  const tipMarkersRef = useRef<Map<string, mapboxgl.Marker>>(new globalThis.Map());
  const saveActionMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const routeBadgesRef = useRef<mapboxgl.Marker[]>([]);
  const routeClickHandlersRef = useRef<Array<{ layerId: string; click: (e: mapboxgl.MapLayerMouseEvent) => void; enter: () => void; leave: () => void }>>([]);
  // PLE-31 follow-up: which classification -> content signature is CURRENTLY
  // drawn on the map, so the draw effect below can leave an unchanged route's
  // layer completely alone instead of flashing it on every incremental update.
  const drawnRouteSigRef = useRef<Map<string, string>>(new globalThis.Map());
  const highlightMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const highlightTimerRef = useRef<number | null>(null);
  const highlightLabelRef = useRef<HTMLDivElement | null>(null);
  // Latest click handler in a ref so the marker DOM listener stays valid
  // without rebuilding the marker on every parent re-render.
  const onSaveClickRef = useRef<(() => void) | undefined>(undefined);
  onSaveClickRef.current = onDestinationSaveClick;
  const onPhotoPinClickRef = useRef<typeof onPhotoPinClick>(undefined);
  onPhotoPinClickRef.current = onPhotoPinClick;
  const onTipPinClickRef = useRef<typeof onTipPinClick>(undefined);
  onTipPinClickRef.current = onTipPinClick;
  const onLongPressRef = useRef<typeof onLongPress>(undefined);
  onLongPressRef.current = onLongPress;
  const userMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const onMapClickRef = useRef<typeof onMapClick>(undefined);
  onMapClickRef.current = onMapClick;
  const onSelectClassificationRef = useRef<typeof onSelectClassification>(undefined);
  onSelectClassificationRef.current = onSelectClassification;
  const lastSavedLocationRef = useRef<string>("");
  const [ready, setReady] = useState(false);
  // WHICH PATH MOVED THE CAMERA (Morgan, Aug 22: "add enough instrumentation
  // to identify which path is firing if it happens again rather than making
  // the bigger change without evidence").
  //
  // A user reports the map re-centring on the destination and zooming in
  // whenever they pan, rotate or zoom. There are eleven camera calls in this
  // file and at least three plausible culprits; guessing between them would
  // mean changing the effect that paints every route on the map on a hunch.
  // So: name every camera move, and record whether the user had touched the
  // map since the trip last changed.
  //
  // This CHANGES NOTHING. It suppresses no camera move and alters no
  // condition. `userMoved` is reported, never enforced — until a log says
  // which path fires, enforcing it would be the same guess in a new place.
  const userMovedRef = useRef(false);
  const cameraLog = (path: string, detail: Record<string, unknown> = {}) => {
    const map = mapRef.current;
    const bits = Object.entries(detail).map(([k, v]) => `${k}=${v}`).join(" ");
    // eslint-disable-next-line no-console
    console.log(
      `[camera] ${path}  zoom=${map?.getZoom?.().toFixed(2) ?? "?"}` +
      `  userMovedSinceTripChange=${userMovedRef.current}${bits ? "  " + bits : ""}`,
    );
  };
  const [styleTick, setStyleTick] = useState(0);
  // Google vs Pleasant diagnostic dialog (#74). Tapping the Google stats
  // pill opens a panel that shows both polylines' coords, lengths, and a
  // Jaccard overlap so the user can see whether the two engines actually
  // chose the same physical path or just look similar at this zoom.
  const [diagOpen, setDiagOpen] = useState(false);
  // "Show bike lanes" overlay (Morgan). User-controlled toggle; fetches the bike
  // infrastructure along the selected route and draws it coloured by type.
  const [showBikeLanes, setShowBikeLanes] = useState(false);

  // Draggable "position the star" marker for tip/photo placement.
  const adjustMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const onAdjustablePinMoveRef = useRef(onAdjustablePinMove);
  onAdjustablePinMoveRef.current = onAdjustablePinMove;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (!adjustablePin) {
      adjustMarkerRef.current?.remove();
      adjustMarkerRef.current = null;
      return;
    }
    if (!adjustMarkerRef.current) {
      const el = document.createElement("div");
      el.style.cssText = "width:44px;height:44px;cursor:grab;filter:drop-shadow(0 3px 5px rgba(0,0,0,.35));";
      el.innerHTML =
        '<svg viewBox="0 0 24 24" width="44" height="44"><path d="M12 2l2.9 6.26 6.6.56-5 4.36 1.5 6.45L12 16.2l-6 3.43 1.5-6.45-5-4.36 6.6-.56z" fill="#f59e0b" stroke="white" stroke-width="1.4" stroke-linejoin="round"/></svg>';
      const m = new mapboxgl.Marker({ element: el, draggable: true, anchor: "center" })
        .setLngLat(adjustablePin)
        .addTo(map);
      m.on("dragend", () => {
        const p = m.getLngLat();
        onAdjustablePinMoveRef.current?.([p.lng, p.lat]);
      });
      adjustMarkerRef.current = m;
    } else {
      adjustMarkerRef.current.setLngLat(adjustablePin);
    }
  }, [adjustablePin, ready]);
  useEffect(() => () => { adjustMarkerRef.current?.remove(); }, []);
  const [bikeLaneSegs, setBikeLaneSegs] = useState<BikeLaneSegment[]>([]);
  // (Morgan) Show bike lanes by default in Bike mode — if you're biking you want
  // to see them without hunting for a toggle. Switching INTO biking turns them
  // on; the toggle still lets you hide them, and that choice sticks until the
  // next time you enter bike mode.
  useEffect(() => {
    if (travelMode === "biking") setShowBikeLanes(true);
  }, [travelMode]);

  // Fetch bike lanes along the selected route whenever the toggle is on (and the
  // route changes). Cleared when toggled off.
  const bikeRouteSig = (selectedRouteCoords ?? routes[0]?.route.coordinates ?? [])
    .length;
  useEffect(() => {
    if (!showBikeLanes) {
      setBikeLaneSegs([]);
      return;
    }
    const coords = selectedRouteCoords ?? routes[0]?.route.coordinates ?? [];
    if (coords.length < 2) {
      setBikeLaneSegs([]);
      return;
    }
    let cancelled = false;
    fetchBikeLanesAlongRoute(coords)
      .then((segs) => { if (!cancelled) setBikeLaneSegs(segs); })
      .catch(() => { if (!cancelled) setBikeLaneSegs([]); });
    return () => { cancelled = true; };
  }, [showBikeLanes, bikeRouteSig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render the bike-lane segments as coloured lines on the map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !map.isStyleLoaded()) return;
    const COLORS: Record<BikeLaneSegment["type"], string> = {
      track: "#16a34a", // protected/separated — green
      lane: "#2563eb",  // painted on-road lane — blue
      path: "#0d9488",  // shared designated path — teal
    };
    const remove = () => {
      // Same teardown guard as removeGoogle (Sentry, Aug 4: getOwnLayer on a
      // dead map, 15 events) — this cleanup can fire after map.remove().
      if (!mapAlive(map)) return;
      for (const t of ["track", "lane", "path"] as const) {
        if (map.getLayer(`bike-${t}`)) map.removeLayer(`bike-${t}`);
      }
      if (map.getSource("bike-lanes")) map.removeSource("bike-lanes");
    };
    remove();
    if (!showBikeLanes || bikeLaneSegs.length === 0) return;
    map.addSource("bike-lanes", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: bikeLaneSegs.map((s) => ({
          type: "Feature" as const,
          properties: { kind: s.type },
          geometry: { type: "LineString" as const, coordinates: s.coords },
        })),
      },
    });
    for (const t of ["path", "lane", "track"] as const) {
      map.addLayer({
        id: `bike-${t}`,
        type: "line",
        source: "bike-lanes",
        filter: ["==", ["get", "kind"], t],
        layout: { "line-join": "round", "line-cap": "round" },
        // Deliberately QUIETER than route lines (Morgan: with the overlay
        // on, the infrastructure scribble — e.g. the Rockefeller trail
        // network — read as "the bike route is a total mess"). Thin,
        // translucent, dashed = reference material, not a route to follow.
        paint: {
          "line-color": COLORS[t],
          "line-width": t === "track" ? 2.5 : 2,
          "line-opacity": 0.55,
          "line-dasharray": [2, 1.5],
        },
      });
    }
    return () => { remove(); };
  }, [showBikeLanes, bikeLaneSegs, ready, styleTick]);

  // GL-WEDGE RECOVERY (the IOGPUMetalError storm in Morgan's log): with
  // the CarPlay keep-alive, the app runs backgrounded and iOS rejects the
  // webview's GPU work — which can permanently wedge the map's context.
  // When the app becomes visible with a wedged map, we rebuild the map
  // outright (bumping this epoch re-runs the init effect on a fresh
  // canvas). Capped so a genuinely broken environment can't loop.
  const [mapEpoch, setMapEpoch] = useState(0);
  const rebuildCountRef = useRef(0);
  /** True once a style has actually finished loading in this session. A map
   *  that has NEVER loaded one isn't wedged — it's still loading. See the
   *  boot-time guard in reviveMap. */
  const styleEverLoadedRef = useRef(false);

  // Diagnostics stay in the console only (the on-screen overlay served its
  // purpose in the great blank-map hunt of July '26 and is retired).
  const pushDiag = (line: string) => console.log(line);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    // Start at the last-known user location (cached) so we don't first
    // download NYC tiles and then jump elsewhere. Falls back to NYC.
    let initialCenter: [number, number] = [-73.9857, 40.7484];
    let initialZoom = 11;
    try {
      const cached = window.localStorage.getItem("pleasant.lastLocation");
      if (cached) {
        const parsed = JSON.parse(cached) as { c: [number, number]; z?: number };
        if (Array.isArray(parsed.c) && parsed.c.length === 2) {
          initialCenter = parsed.c;
          initialZoom = parsed.z ?? 13;
        }
      }
    } catch { /* No remembered camera, or unreadable storage (private mode,
                 opaque origin). The defaults above already stand in. */ }

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: PLEASANT_LIGHT_STYLE,
      center: initialCenter,
      zoom: initialZoom,
      // `{}` rather than `true`: maplibre 5 types this as
      // `false | AttributionControlOptions`. Behaviour is unchanged — the
      // runtime maps a boolean `true` to `undefined`, and both of
      // AttributionControlOptions' fields are optional, so `{}` and
      // `undefined` build the same default control.
      attributionControl: {},
      // Faster first paint: skip the fade-in animation on tiles.
      fadeDuration: 0,
    });
    // Survive our own teardown: after remove(), style-touching calls
    // return empty instead of throwing through the error boundary.
    // See src/utils/mapTeardown.ts for why this is one call and not 64.
    hardenMapAgainstTeardown(map, "MapView");
    // NavigationControl (zoom +/-) intentionally omitted — pinch / scroll
    // gestures cover zoom, and the on-map "+" was visually competing with
    // the Add-tip pin button.
    map.on("load", () => setReady(true));
    // [perf] logging only — should read ~0/s while navigation covers this map.
    map.on("render", () => { navPerf.planMapRenders += 1; });
    mapRef.current = map;

    // BLANK-MAP DIAGNOSTICS (temporary): one run of these three lines in
    // the Xcode console names the culprit — fetch errors, missing tiles,
    // or a zero-size canvas.
    // Engine + graphics reality check: which maplibre is actually running,
    // and can this webview create a WebGL context at all?
    try {
      const v = (mapboxgl as unknown as { version?: string }).version ?? "?";
      const test = document.createElement("canvas");
      const gl = test.getContext("webgl2") ?? test.getContext("webgl");
      pushDiag(`PLEASANT MAP init: maplibre v${v} webgl=${gl ? "OK" : "UNAVAILABLE"}`);
    } catch (err) {
      pushDiag(`PLEASANT MAP init check failed: ${String(err)}`);
    }
    map.on("error", (e) => {
      const msg = (e as { error?: { message?: string } })?.error?.message ?? String(e);
      pushDiag(`PLEASANT MAP ERROR: ${msg}`);
    });
    map.on("styledata", () => {
      try { if (map.isStyleLoaded()) styleEverLoadedRef.current = true; } catch { /* ok */ }
    });
    map.once("idle", () => {
      try {
        if (map.isStyleLoaded()) styleEverLoadedRef.current = true;
        const c = map.getCanvas();
        pushDiag(
          `PLEASANT MAP IDLE: styleLoaded=${map.isStyleLoaded()} zoom=${map.getZoom().toFixed(1)} ` +
          `canvas=${c.width}x${c.height}`,
        );
      } catch { /* diag only */ }
    });
    const diagT8 = window.setTimeout(() => {
      try {
        const c = map.getCanvas();
        pushDiag(`PLEASANT MAP 8s: canvas=${c.width}x${c.height} styleLoaded=${map.isStyleLoaded()}`);
      } catch { /* diag only */ }
    }, 8000);
    // ZERO-CANVAS WATCHDOG (the CAMetalLayer 0x0 line in Morgan's log):
    // when the drawing surface comes up empty at boot, keep nudging the
    // map to adopt its container's real size until it does.
    let watchdogTicks = 0;
    const canvasWatchdog = window.setInterval(() => {
      watchdogTicks += 1;
      try {
        const m = mapRef.current;
        if (!m) { window.clearInterval(canvasWatchdog); return; }
        const c = m.getCanvas();
        if (c.width === 0 || c.height === 0) {
          pushDiag("PLEASANT MAP WATCHDOG: zero canvas — forcing resize");
          m.resize();
        } else if (watchdogTicks >= 5) {
          window.clearInterval(canvasWatchdog);
        }
      } catch { window.clearInterval(canvasWatchdog); }
    }, 2000);

    // WHITE-MAP-AFTER-BACKGROUND (Morgan: "it's all white"): first try the
    // gentle poke (resize + repaint); if the style still isn't loaded a
    // moment later, the GL context is wedged — rebuild the whole map.
    const reviveMap = () => {
      setTimeout(() => {
        try {
          mapRef.current?.resize();
          (mapRef.current as unknown as { triggerRepaint?: () => void })?.triggerRepaint?.();
        } catch { /* map gone */ }
      }, 250);
      setTimeout(() => {
        try {
          const m = mapRef.current;
          // A FIRST LOAD IS NOT A WEDGE (Morgan, Aug 9: "it's really slow and
          // i have to keep clicking on it").
          //
          // This check was written for the resume case — "it's all white"
          // after backgrounding — where the style is already cached and 1.6s
          // is generous. But reviveMap also runs unconditionally at boot (the
          // visibilityState check below), and there 1.6s is a race against the
          // tile server. On a cold load from OpenFreeMap the style routinely
          // isn't ready yet, so the app tore down the map it had just built
          // and started over, up to three times. Her log shows it: two
          // "MAP init" lines, "REBUILD: context wedged" between them, then
          // "There is no style added to the map" and styleLoaded=undefined at
          // the 8s mark. The rebuild loop WAS the slowness.
          //
          // A wedge means a style that loaded and then stopped working. A map
          // that has never had one is simply still loading, and rebuilding it
          // only restarts the download.
          if (m && !m.isStyleLoaded() && styleEverLoadedRef.current && rebuildCountRef.current < 3) {
            rebuildCountRef.current += 1;
            pushDiag("PLEASANT MAP REBUILD: context wedged — recreating map");
            setMapEpoch((e) => e + 1);
          } else if (m && !m.isStyleLoaded() && !styleEverLoadedRef.current) {
            pushDiag("PLEASANT MAP: style still loading at 1.6s — not a wedge, waiting");
          }
        } catch { /* map gone */ }
      }, 1600);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") reviveMap();
    };
    document.addEventListener("visibilitychange", onVisibility);
    let resumeHandle: { remove: () => void } | undefined;
    void import("@capacitor/app")
      .then(({ App }) =>
        App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) {
            reviveMap();
          } else {
            // Going background: stop any camera animation so the render
            // loop quiets down instead of hammering forbidden GPU frames.
            try { mapRef.current?.stop(); } catch { /* ok */ }
          }
        }),
      )
      .then((h) => { resumeHandle = h; })
      .catch(() => {});
    // Boot-under-lock case: if the app was launched by the car with the
    // phone locked, the first map may be born wedged — check once the
    // screen is actually being looked at.
    if (document.visibilityState === "visible") reviveMap();
    const cleanupResume = () => {
      document.removeEventListener("visibilitychange", onVisibility);
      try { resumeHandle?.remove(); } catch { /* ok */ }
    };

    // Notify popovers / floating UI that the map is being interacted with so
    // they can dismiss themselves before they end up visually detached from
    // their trigger. We dispatch a custom window event for each gesture
    // start (pan, zoom, rotate, pitch, drag).
    const fireMapInteract = (e?: { originalEvent?: unknown }) => {
      // Only a REAL finger/mouse gesture sets the flag. Our own easeTo/flyTo
      // fire movestart too, and counting those as "the user moved" would make
      // the flag lie in precisely the case we are trying to diagnose. A
      // programmatic camera move carries no originalEvent; a gesture does.
      // (Same discriminator NavigationView.tsx:2151 already uses.)
      if (e?.originalEvent) userMovedRef.current = true;
      // The popover-dismiss event stays unconditional — that behaviour is
      // correct for programmatic moves as well and is not ours to change here.
      try {
        window.dispatchEvent(new CustomEvent("map:interact"));
      } catch { /* Nothing is required to be listening. A missing CustomEvent
                   constructor must not take the map gesture down with it. */ }
    };
    map.on("movestart", fireMapInteract);
    map.on("zoomstart", fireMapInteract);
    map.on("rotatestart", fireMapInteract);
    map.on("pitchstart", fireMapInteract);
    map.on("dragstart", fireMapInteract);

    // Belt-and-braces: if the container's size wasn't final at init time
    // (common when this mounts inside a flex/absolute layout that lays out
    // a frame later), force a resize so the map paints tiles for the real
    // viewport instead of a stale 0×0 measurement.
    const resizeRaf = requestAnimationFrame(() => map.resize());
    const resizeT1 = setTimeout(() => map.resize(), 250);
    const resizeT2 = setTimeout(() => map.resize(), 1000);
    const onWinResize = () => map.resize();
    window.addEventListener("resize", onWinResize);

    return () => {
      cancelAnimationFrame(resizeRaf);
      window.clearInterval(canvasWatchdog);
      window.clearTimeout(diagT8);
      clearTimeout(resizeT1);
      clearTimeout(resizeT2);
      window.removeEventListener("resize", onWinResize);
      cleanupResume();
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapEpoch]);

  // Attach the map click handler ONCE when the map is ready. The handler
  // dispatches via a ref so re-renders that pass a new inline `onMapClick`
  // do not re-bind the listener (which previously fired on every render).
  useEffect(() => {
    if (!mapRef.current || !ready) return;
    const handler = (e: mapboxgl.MapMouseEvent) =>
      onMapClickRef.current?.([e.lngLat.lng, e.lngLat.lat]);
    mapRef.current.on("click", handler);
    return () => { mapRef.current?.off("click", handler); };
  }, [ready]);

  // Build a stable signature for the route SHAPES so we only rebuild
  // sources/markers/bounds when the routes themselves change — not when
  // the user simply taps a different route card (which only flips
  // `selectedClassification`).
  const routesSig = useMemo(() => {
    if (routes.length === 0) return "";
    return routes
      .map((r) => `${r.classification}:${r.route.coordinates.length}:${Math.round(r.route.distance)}`)
      .join("|");
  }, [routes]);

  // Draw routes (sources + markers + fit bounds) — only when routes change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    // During HMR / rapid style reloads, `ready` can be true while the style
    // is mid-swap. Calling addSource then throws "Style is not done loading".
    // Defer to the next styledata tick when that happens.
    //
    // PLE-31 (measured on the PLE-10 overheating-branch build, real device):
    // `styledata` can simply not fire for a long stretch when the main thread
    // is pegged by route search/enrichment work — one captured run saw 72s of
    // silence between this defer and the next `[routeDraw]` line, during
    // which a route card had already moved on to a different candidate while
    // its line on the map kept showing the stale, now-mismatched geometry.
    // A deferred draw must not depend solely on an event that can be
    // starved — retry on a short timer too, so a stuck style self-heals in
    // ~1s instead of waiting indefinitely on a `styledata` that may not come.
    if (!map.isStyleLoaded()) {
      // eslint-disable-next-line no-console
      console.log(`[routeDraw] DEFERRED style-not-loaded — pending=${routesSig}`);
      const retry = () => setStyleTick((n) => n + 1);
      map.once("styledata", retry);
      const timer = window.setTimeout(retry, 1000);
      return () => {
        map.off("styledata", retry);
        window.clearTimeout(timer);
      };
    }

    if (!mapAlive(map)) return;

    // Reconcile route layers by identity instead of tearing down all three on
    // every run (user report, Oct 2: "the routes now flash on screen as they
    // refresh"). `routesSig` — and so this whole effect — reruns whenever ANY
    // route's numbers change, including an already-drawn route quietly
    // refining its estimate well after its card was showing (e.g. Fastest
    // going from a mapbox-direct guess to google-direct). The old code
    // removed+re-added ALL THREE layers on that single change, flashing the
    // two that hadn't moved. Same fix as the Aug 26 pin-blink bug below:
    // touch nothing whose signature hasn't changed.
    const wantedSigs = new globalThis.Map<string, string>();
    for (const r of routes) {
      const congestionKey = r.route.congestion?.length ? r.route.congestion.join(",") : "";
      wantedSigs.set(
        r.classification,
        `${r.route.coordinates.length}:${Math.round(r.route.distance)}:${congestionKey}`,
      );
    }
    const removeRouteLayer = (cls: string) => {
      const hitId = `route-${cls}-hit`;
      routeClickHandlersRef.current = routeClickHandlersRef.current.filter((h) => {
        if (h.layerId !== hitId) return true;
        map.off("click", h.layerId, h.click);
        map.off("mouseenter", h.layerId, h.enter);
        map.off("mouseleave", h.layerId, h.leave);
        return false;
      });
      if (map.getLayer(hitId)) map.removeLayer(hitId);
      if (map.getLayer(`route-${cls}`)) map.removeLayer(`route-${cls}`);
      if (map.getSource(`route-${cls}`)) map.removeSource(`route-${cls}`);
    };
    // Drop any classification that disappeared or whose signature changed —
    // its stale layer must go before anything replaces it. Anything left in
    // drawnRouteSigRef after this loop is unchanged and gets skipped below.
    for (const [cls, prevSig] of [...drawnRouteSigRef.current]) {
      const wanted = wantedSigs.get(cls);
      if (wanted === undefined || wanted !== prevSig) {
        removeRouteLayer(cls);
        drawnRouteSigRef.current.delete(cls);
      }
    }

    // NOTE: no early return for an empty route set. The route layers are gone
    // by now, and the loop below no-ops, but the markers further down must
    // still be REBUILT rather than skipped — see the marker section.

    // Split a route into runs of like congestion so the SELECTED route's line
    // can show live traffic (clear stretches keep the route's colour; moderate/
    // heavy/severe turn yellow/orange/red — the Google/Waze pattern). Returns a
    // FeatureCollection where each feature carries a `congestion` property.
    const congestionFC = (
      coords: LngLat[],
      congestion: string[],
    ): GeoJSON.FeatureCollection => {
      const norm = (c?: string) =>
        c === "moderate" || c === "heavy" || c === "severe" ? c : "low";
      const n = Math.min(congestion.length, coords.length - 1);
      const features: GeoJSON.Feature[] = [];
      let segStart = 0;
      let cur = norm(congestion[0]);
      for (let i = 1; i <= n; i++) {
        const lvl = i < n ? norm(congestion[i]) : null;
        if (lvl !== cur) {
          features.push({
            type: "Feature",
            properties: { congestion: cur },
            geometry: { type: "LineString", coordinates: coords.slice(segStart, i + 1) },
          });
          segStart = i;
          cur = lvl;
        }
      }
      return { type: "FeatureCollection", features };
    };

    // Draw bottom-up so selected is on top
    const order: ("fastest" | "scenic" | "pleasant")[] = ["fastest", "scenic", "pleasant"];
    for (const cls of order) {
      const r = routes.find((x) => x.classification === cls);
      if (!r) continue;
      // Unchanged since the last draw — already on the map, untouched above.
      // Redrawing it here would be the exact flash this reconciliation exists
      // to prevent.
      if (drawnRouteSigRef.current.get(cls) === wantedSigs.get(cls)) continue;
      const isSelected = cls === selectedClassification;
      // Traffic colouring for EVERY route that has per-segment congestion
      // (driving-traffic profile) — not just the selected one. (Morgan: the
      // Direct line needs its congestion shown too, so you can see at a glance
      // that the fast route is the one stuck in traffic.) Non-selected lines
      // stay dimmer (opacity 0.75) so the selected route still reads as primary.
      const congestion = r.route.congestion;
      const useTraffic = !!congestion && congestion.length > 0;
      map.addSource(`route-${cls}`, {
        type: "geojson",
        data: useTraffic
          ? congestionFC(r.route.coordinates, congestion!)
          : {
              type: "Feature",
              geometry: { type: "LineString", coordinates: r.route.coordinates },
              properties: {},
            },
      });
      map.addLayer({
        id: `route-${cls}`,
        type: "line",
        source: `route-${cls}`,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          // On the selected route, colour by live congestion (clear stretches
          // keep the route's class colour; busy stretches go yellow→orange→red).
          "line-color": useTraffic
            ? ([
                "match",
                ["get", "congestion"],
                "moderate", "#f5b301",
                "heavy", "#f97316",
                "severe", "#ef4444",
                COLORS[cls],
              ] as unknown as string)
            : COLORS[cls],
          // (#150) Bumped non-selected width 3 → 5 and opacity 0.55 → 0.75
          // so the non-selected lines (Direct + Scenic when Pleasant is
          // the active card) are actually readable on the map. Selected
          // line still pops at width 6/7 + opacity 1. The previous values
          // meant the Direct polyline was effectively invisible.
          "line-width": cls === "pleasant" ? (isSelected ? 7 : 5) : isSelected ? 6 : 5,
          "line-opacity": isSelected ? 1 : 0.75,
        },
      });

      // Clickable route line → select that classification. We also widen
      // the hit area with an invisible thicker line on top so taps on
      // mobile are forgiving without changing the visual stroke width.
      const hitId = `route-${cls}-hit`;
      if (map.getLayer(hitId)) map.removeLayer(hitId);
      map.addLayer({
        id: hitId,
        type: "line",
        source: `route-${cls}`,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": COLORS[cls], "line-width": 22, "line-opacity": 0 },
      });
      const onClick = (e: mapboxgl.MapLayerMouseEvent) => {
        e.originalEvent?.stopPropagation();
        onSelectClassificationRef.current?.(cls);
      };
      const onEnter = () => { map.getCanvas().style.cursor = "pointer"; };
      const onLeave = () => { map.getCanvas().style.cursor = ""; };
      map.on("click", hitId, onClick);
      map.on("mouseenter", hitId, onEnter);
      map.on("mouseleave", hitId, onLeave);
      routeClickHandlersRef.current.push({ layerId: hitId, click: onClick, enter: onEnter, leave: onLeave });
      drawnRouteSigRef.current.set(cls, wantedSigs.get(cls)!);
    }

    // Floating "Through {town}" route badges were removed — they were confusing
    // on the map (duplicated, and ambiguous about which line they belonged to).
    // We still clear any that a previous render placed so none linger.
    routeBadgesRef.current.forEach((m) => m.remove());
    routeBadgesRef.current = [];

    // TRIP PINS — RECONCILED BY IDENTITY, NEVER REBUILT (Morgan, Aug 26:
    // "the start/end pins still visibly blink when I switch from Drive to
    // Bike" — a failed acceptance test of the previous fix).
    //
    // MEASURED, not reasoned about. One Drive → Bike switch ran this block
    // three times in the harness and destroyed + recreated EVERY pin on every
    // pass: 9 DOM nodes for 3 pins that never moved. The real app is worse —
    // a generation writes `scored` from up to eight places in generateRoutes,
    // seconds apart, and each write is its own React commit in its own frame,
    // with styleTick bumps from the bike-lane layers landing in between.
    //
    // The previous comment here claimed remove-then-add was "synchronous, so
    // unchanged endpoints do not flicker". That was the wrong question. One
    // swap may well be atomic; a pin whose DOM node is replaced N times across
    // N frames blinks N times regardless. Clearing the cards never caused this
    // — every routesSig change always did, and always had.
    //
    // So: touch nothing that hasn't changed. Each pin carries a key encoding
    // everything that affects it (kind, position, label), a pass removes only
    // keys that went away and creates only keys that appeared, and a pin that
    // stayed put is left completely alone — there is no node to blink. Same
    // pattern the photo pins below already use, for the same reason.
    const wantedPins: { key: string; make: () => mapboxgl.Marker }[] = [];
    if (start) {
      wantedPins.push({
        key: `start:${start.join(",")}`,
        make: () => {
          const el = document.createElement("div");
          el.className = "w-4 h-4 rounded-full bg-foreground border-2 border-background shadow-md";
          return new mapboxgl.Marker({ element: el }).setLngLat(start).addTo(map);
        },
      });
    }
    if (end) {
      wantedPins.push({
        key: `end:${end.join(",")}`,
        make: () => {
          const el = document.createElement("div");
          el.className = "w-5 h-5 rounded-full border-2 border-background shadow-md";
          el.style.background = COLORS.pleasant;
          return new mapboxgl.Marker({ element: el }).setLngLat(end).addTo(map);
        },
      });
    }
    // Star markers for intermediate stops (waypoints / drive-by picks).
    for (const stop of stops) {
      // Sentry 7671786936 (Aug 14, release 911652d8): `stop` itself was
      // undefined — a hole in the stops array, not a missing name. One user,
      // handled, but a hole should cost one missing star, not an error.
      if (!stop) continue;
      const name = stop.name ?? "Stop";
      wantedPins.push({
        key: `stop:${stop.coords.join(",")}:${name}`,
        make: () => {
          const el = document.createElement("div");
          el.className = "flex items-center justify-center w-7 h-7 rounded-full border-2 border-background shadow-md cursor-pointer";
          el.style.background = "hsl(45 95% 55%)";
          el.title = name;
          el.innerHTML = `<svg viewBox="0 0 24 24" width="14" height="14" fill="white" stroke="white" stroke-width="1.5" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;
          const popup = new mapboxgl.Popup({ offset: 18, closeButton: false }).setHTML(
            `<div style="font-size:12px;font-weight:600;padding:2px 4px;">${name.replace(/</g, "&lt;")}</div>`
          );
          return new mapboxgl.Marker({ element: el }).setLngLat(stop.coords).setPopup(popup).addTo(map);
        },
      });
    }
    // Sparkle markers for auto-applied pleasant detours. Distinct visual
    // from user-added stops so the user understands "we routed past this
    // *for you*" — and the popup names the place + reason.
    for (const d of detourMarkers) {
      const safeName = (d.name ?? "Pleasant detour").replace(/</g, "&lt;");
      const safeReason = (d.reason ?? "").replace(/</g, "&lt;");
      wantedPins.push({
        key: `detour:${d.coords.join(",")}:${safeName}:${safeReason}`,
        make: () => {
          const el = document.createElement("div");
          el.className = "flex items-center justify-center w-7 h-7 rounded-full border-2 border-background shadow-md cursor-pointer";
          el.style.background = COLORS.pleasant;
          el.title = d.name ?? "Pleasant detour";
          el.innerHTML = `<svg viewBox="0 0 24 24" width="14" height="14" fill="white"><path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6L12 2z"/></svg>`;
          const popup = new mapboxgl.Popup({ offset: 18, closeButton: false }).setHTML(
            `<div style="font-size:12px;padding:2px 4px;max-width:200px;">
          <div style="font-weight:600;margin-bottom:2px;">${safeName}</div>
          ${safeReason ? `<div style="color:#555;font-size:11px;">${safeReason}</div>` : ""}
        </div>`,
          );
          return new mapboxgl.Marker({ element: el }).setLngLat(d.coords).setPopup(popup).addTo(map);
        },
      });
    }
    {
      const wantedKeys = new Set(wantedPins.map((p) => p.key));
      for (const [key, marker] of [...tripPinsRef.current]) {
        if (!wantedKeys.has(key)) {
          marker.remove();
          tripPinsRef.current.delete(key);
        }
      }
      for (const pin of wantedPins) {
        if (!tripPinsRef.current.has(pin.key)) tripPinsRef.current.set(pin.key, pin.make());
      }
    }

    // No routes to frame (a fresh search, or the gap during a travel-mode
    // switch): the markers above are already at the current trip's endpoints,
    // so leave the camera alone. Moving it here would drag the map once now and
    // again the moment the new routes land — the "it moves, then disappears"
    // half of the stale-map bug.
    if (routes.length === 0) return;

    // Fit bounds — ONLY when the route set actually changes, not when the
    // user just switches between fastest / pleasant / scenic.
    const allCoords = routes.flatMap((r) => r.route.coordinates);
    if (allCoords.length) {
      cameraLog("routeDraw/fitToRoutes", { routes: routes.length });
      const b = bbox(allCoords, 0.01);
      map.fitBounds(
        [[b.minLng, b.minLat], [b.maxLng, b.maxLat]],
        { padding: { top: 80, bottom: 80, left: 60, right: 60 }, duration: 800 },
      );
    } else if (start && end) {
      // Both endpoints chosen but no route yet — frame both so the user
      // immediately sees where they're going relative to the start.
      cameraLog("routeDraw/fitToEndpoints");
      const b = bbox([start, end], 0.05);
      map.fitBounds(
        [[b.minLng, b.minLat], [b.maxLng, b.maxLat]],
        { padding: { top: 100, bottom: 200, left: 60, right: 60 }, duration: 800 },
      );
    } else if (end) {
      // Destination typed/picked first: recenter on it so the map reflects
      // where the user is searching, not just their current GPS.
      // RATCHET: Math.max means this can only zoom IN, never back out.
      cameraLog("routeDraw/flyToEnd", { ratchetFloor: 13 });
      map.flyTo({ center: end, zoom: Math.max(map.getZoom(), 13), duration: 800 });
    } else if (start) {
      // Standalone start pin (e.g., from Explore "Best places now" or after
      // picking a saved place as the starting point): always recenter on it.
      // RATCHET: as above.
      cameraLog("routeDraw/flyToStart", { ratchetFloor: 13 });
      map.flyTo({ center: start, zoom: Math.max(map.getZoom(), 13), duration: 800 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routesSig, ready, start, end, styleTick, stops.map(s => `${s.coords[0]},${s.coords[1]}`).join("|"), detourMarkers.map(d => `${d.coords[0]},${d.coords[1]}`).join("|")]);

  // Cheap restyle pass: when only the selected classification changes,
  // update line widths/opacity on the existing layers without tearing down
  // sources or refitting bounds (avoids the 800ms fit-bounds animation on
  // every route-card tap).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || routes.length === 0) return;
    // TEARDOWN GUARD (Sentry, Aug 6: getLayer on a dead map from THIS
    // effect — tap a route, navigate to /auth, restyle fires ms after
    // map.remove()). Every other layer-touching effect already checks
    // mapAlive; this one checked `ready` but not aliveness.
    if (!mapAlive(map)) return;
    const order: ("fastest" | "scenic" | "pleasant")[] = ["fastest", "scenic", "pleasant"];
    for (const cls of order) {
      if (!map.getLayer(`route-${cls}`)) continue;
      const isSelected = cls === selectedClassification;
      map.setPaintProperty(
        `route-${cls}`,
        "line-width",
        cls === "pleasant" ? (isSelected ? 7 : 5) : isSelected ? 6 : 3,
      );
      map.setPaintProperty(
        `route-${cls}`,
        "line-opacity",
        isSelected ? 1 : 0.55,
      );
    }
  }, [selectedClassification, routesSig, ready, routes.length]);

  // Google reference polyline overlay — toggleable via the floating button.
  // Rendered as a dashed Google-blue line with a white casing so it reads as
  // "the other app's route" without competing with our scored options.
  const googleSig = useMemo(() => {
    if (!googleRouteCoords?.length) return "";
    const a = googleRouteCoords[0];
    const b = googleRouteCoords[googleRouteCoords.length - 1];
    return `${googleRouteCoords.length}:${a?.[0]},${a?.[1]}:${b?.[0]},${b?.[1]}`;
  }, [googleRouteCoords]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    // THE BLUE LINE THAT NEVER CAME BACK (user report, Aug 22).
    //
    // This used to be a bare `return`. The cleanup below ALWAYS removes the
    // Google layers, and `isStyleLoaded()` is false while ANY tile is still
    // loading — i.e. during and just after every pan and zoom. So a re-run
    // that landed mid-gesture removed the line and then bailed before
    // re-adding it, and nothing ever put it back.
    //
    // Defer instead of bail, exactly as the route-draw effect above already
    // does: re-run on the next styledata tick and redraw properly.
    if (!map.isStyleLoaded()) {
      const retry = () => setStyleTick((n) => n + 1);
      map.once("styledata", retry);
      return () => { map.off("styledata", retry); };
    }

    const removeGoogle = () => {
      // TEARDOWN GUARD (Sentry, Aug 4: getLayer on a dead map, 3 events from
      // this exact function): the cleanup below runs on unmount/deps change,
      // which can land AFTER the GL-wedge rebuild or CarPlay backgrounding
      // destroyed the map. Same fix as everywhere else: mapAlive first.
      if (!mapAlive(map)) return;
      if (map.getLayer("route-google-casing")) map.removeLayer("route-google-casing");
      if (map.getLayer("route-google")) map.removeLayer("route-google");
      if (map.getSource("route-google")) map.removeSource("route-google");
    };

    removeGoogle();

    if (!showGoogleRoute || !googleRouteCoords || googleRouteCoords.length < 2) return;

    map.addSource("route-google", {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: { type: "LineString", coordinates: googleRouteCoords },
        properties: {},
      },
    });
    map.addLayer({
      id: "route-google-casing",
      type: "line",
      source: "route-google",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#ffffff", "line-width": 7, "line-opacity": 0.85 },
    });
    map.addLayer({
      id: "route-google",
      type: "line",
      source: "route-google",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": "#4285F4",
        "line-width": 4,
        "line-opacity": 0.95,
        "line-dasharray": [2, 1.5],
      },
    });

    return () => { removeGoogle(); };
  }, [googleSig, showGoogleRoute, ready, styleTick, routesSig, googleRouteCoords]);

  // (Removed) Floating "Save this place" pill above the destination pin.
  // Labeling now happens via the dedicated entry in the saved-places strip
  // and the search dropdown — keeps the map clean.
  useEffect(() => {
    saveActionMarkerRef.current?.remove();
    saveActionMarkerRef.current = null;
  }, [end, ready, destinationSaveLabel, onDestinationSaveClick]);

  // Community tag dots — clickable, with a popup describing what was reported.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    tagMarkersRef.current.forEach((m) => m.remove());
    tagPopupsRef.current.forEach((p) => p.remove());
    tagMarkersRef.current.clear();
    tagPopupsRef.current.clear();
    if (!showCommunity) return;

    const fmtAge = (iso: string): string => {
      const ms = Date.now() - new Date(iso).getTime();
      const days = Math.floor(ms / 86_400_000);
      if (days < 1) return "today";
      if (days === 1) return "yesterday";
      if (days < 30) return `${days}d ago`;
      const months = Math.floor(days / 30);
      return months <= 1 ? "1mo ago" : `${months}mo ago`;
    };

    for (const t of communityTags) {
      const el = document.createElement("div");
      el.className =
        "w-3 h-3 rounded-full border border-background shadow-sm cursor-pointer hover:scale-125 transition-transform";
      const color = t.sentiment === "positive" ? "hsl(172 55% 42%)" : "hsl(18 70% 55%)";
      el.style.background = color;
      el.title = `${t.tag_label} · ${t.sentiment}`;

      const sentimentChip = t.sentiment === "positive"
        ? '<span style="background:hsl(172 55% 42%);color:white;padding:1px 6px;border-radius:999px;font-size:10px;font-weight:600;">Pleasant</span>'
        : '<span style="background:hsl(18 70% 55%);color:white;padding:1px 6px;border-radius:999px;font-size:10px;font-weight:600;">Unpleasant</span>';
      const kindLine = t.report_kind
        ? `<div style="font-size:10px;color:hsl(0 0% 45%);margin-top:2px;text-transform:capitalize;">${t.report_kind.replace(/_/g, " ")}</div>`
        : "";
      const html = `
        <div style="font-family:inherit;min-width:170px;padding:2px 2px 4px;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">${sentimentChip}<span style="font-size:11px;color:hsl(0 0% 45%);">${fmtAge(t.created_at)}</span></div>
          <div style="font-size:13px;font-weight:600;color:hsl(0 0% 12%);line-height:1.25;">${t.tag_label}</div>
          ${kindLine}
        </div>`;
      const popup = new mapboxgl.Popup({ offset: 12, closeButton: false, maxWidth: "240px" }).setHTML(html);
      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([t.lng, t.lat])
        .setPopup(popup)
        .addTo(map);
      tagMarkersRef.current.set(t.id, marker);
      tagPopupsRef.current.set(t.id, popup);
    }
  }, [communityTags, showCommunity, ready]);

  // Pan + open popup when the host requests focus on a specific signal.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !focusedTagId) return;
    const marker = tagMarkersRef.current.get(focusedTagId);
    const popup = tagPopupsRef.current.get(focusedTagId);
    if (!marker) return;
    const ll = marker.getLngLat();
    cameraLog("focusedTag/flyTo", { tag: focusedTagId, ratchetFloor: 15 });
    map.flyTo({ center: [ll.lng, ll.lat], zoom: Math.max(map.getZoom(), 15), duration: 700 });
    // Close any other open popups, then open this one.
    tagPopupsRef.current.forEach((p, id) => { if (id !== focusedTagId) p.remove(); });
    if (popup && !popup.isOpen()) marker.togglePopup();
  }, [focusedTagId, ready, communityTags]);

  // -------------------------------------------------------------------------
  // PLACE / POI PINS — OWNED BY A ROUTE, RECONCILED BY IDENTITY (Aug 27 2026).
  //
  // Morgan: coffee/POI markers still go stale, still move when they shouldn't,
  // still blink. The trip pins were fixed on Aug 26 (e68e5d4e) and the route
  // lines the day before; this block was left on the OLD pattern — an array,
  // wiped and rebuilt from scratch on every pass — which is the same defect
  // twice over:
  //
  //   BLINK. The dependency list was the raw `routes` ARRAY. `generateRoutes`
  //   writes `scored` from eight places seconds apart, each its own React
  //   commit, and `detourByClass` produces new route objects on top. Every one
  //   of those re-runs destroyed and recreated every café pin, so a pin that
  //   never moved was replaced N times across N frames. A node replaced N
  //   times blinks N times, exactly as the trip pins did.
  //
  //   STALE / WRONG-ROUTE. Nothing tied a marker to the route it came from.
  //   Removal depended on this effect re-running with a live map — so the
  //   `!map || !ready` guard ABOVE the teardown could skip it (a style reload
  //   or map rebuild during a mode switch), and a pin from the previous route
  //   or the previous travel mode stayed on screen over the new line.
  //
  // The fix is ownership in the KEY. Each pin's key carries the route that
  // produced it — mode, classification, geometry length, distance — plus the
  // place's own id and position. So "the active route changed" and "this pin
  // must go" are the same fact: every old key disappears and the reconcile
  // removes exactly those markers, with no separate clearing step that a guard
  // can skip. A pin whose owner and position are unchanged is never touched.
  //
  // Same pattern as tripPinsRef and the photo pins. Not a routing rule: no
  // threshold, filter or ranking moved — PIN_BUFFER_M and MAX_PER_TYPE are
  // unchanged, and the set of pins for a given route is identical.
  // -------------------------------------------------------------------------

  /** Everything about the ACTIVE ROUTE that a pin belongs to. When any part of
   *  this changes, every pin key changes, and every old pin is removed. */
  const placeOwnerKey = useMemo(() => {
    const sel = routes.find((r) => r.classification === selectedClassification);
    if (!sel) return "";   // no active route → owns nothing → all pins go
    return [
      travelMode,
      sel.classification,
      sel.route.coordinates.length,
      Math.round(sel.route.distance),
    ].join(":");
  }, [routes, selectedClassification, travelMode]);

  /** The pins themselves, as a signature — so a re-render that changes no pin
   *  does not re-run the effect at all. Places arrive from enrichment and are
   *  stable thereafter; `id` is Google's own. */
  const placePinsSig = useMemo(() => {
    const sel = routes.find((r) => r.classification === selectedClassification);
    const places = sel?.googlePlaces ?? [];
    return `${placeOwnerKey}#${places.length}#${places.map((p) => p.id).join(",")}`;
  }, [routes, selectedClassification, placeOwnerKey]);

  useEffect(() => {
    const map = mapRef.current;
    const live = placeMarkersRef.current;

    // TEARDOWN FIRST, AND UNCONDITIONALLY. The old code returned before
    // clearing when the map was gone or not ready, which is precisely the
    // window a mode switch opens — the map reloads its style for the bike-lane
    // layers while the route set is being replaced. Markers belonging to a map
    // we can no longer address are dropped here rather than left addressed to
    // nothing.
    if (!map || !ready) {
      for (const [key, m] of [...live]) { m.remove(); live.delete(key); }
      return;
    }

    const selected = routes.find((r) => r.classification === selectedClassification);
    const places = selected?.googlePlaces ?? [];
    const routeCoords = selected?.route.coordinates ?? [];
    const PIN_BUFFER_M = 300;

    // Cap pins per type so dense urban routes don't render hundreds of dots.
    const MAX_PER_TYPE = 25;
    const byType = new Map<GooglePlaceHit["type"], GooglePlaceHit[]>();
    if (selected && routeCoords.length >= 2) {
      for (const p of places) {
        if (!PLACE_PIN_STYLE[p.type]) continue;
        // Only show pins close to THIS route's polyline.
        const d = nearestPointOnPolyline(p.center, routeCoords).distance;
        if (d > PIN_BUFFER_M) continue;
        const arr = byType.get(p.type) ?? [];
        arr.push(p);
        byType.set(p.type, arr);
      }
    }

    const wanted: { key: string; make: () => mapboxgl.Marker }[] = [];
    for (const [type, arr] of byType) {
      // Highest-rated first, then by review count — capped per type.
      arr.sort((a, b) =>
        b.rating !== a.rating ? b.rating - a.rating : b.userRatingCount - a.userRatingCount,
      );
      const style = PLACE_PIN_STYLE[type]!;
      for (const p of arr.slice(0, MAX_PER_TYPE)) {
        wanted.push({
          // OWNER FIRST. `placeOwnerKey` changes the moment the active route
          // or the travel mode does, so no key can outlive its route.
          key: `${placeOwnerKey}|${type}|${p.id}|${p.center[0]},${p.center[1]}`,
          make: () => {
            const el = document.createElement("button");
            el.type = "button";
            el.setAttribute("aria-label", `${style.label}: ${p.name}`);
            el.style.cssText =
              "all: unset; cursor: pointer; width: 24px; height: 24px; border-radius: 9999px;" +
              `background: ${style.bg};` +
              "border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3);" +
              "display: flex; align-items: center; justify-content: center;" +
              "font-size: 13px; line-height: 1; transition: transform 120ms;";
            el.textContent = style.glyph;
            el.addEventListener("mouseenter", () => { el.style.transform = "scale(1.15)"; });
            el.addEventListener("mouseleave", () => { el.style.transform = "scale(1)"; });

            const popupHtml = `
          <div style="font-family: inherit; min-width: 160px;">
            <div style="font-weight: 600; font-size: 13px; margin-bottom: 2px;">${escapeHtml(p.name)}</div>
            <div style="font-size: 11px; color: #555;">
              ${style.label} · ★ ${p.rating.toFixed(1)} (${p.userRatingCount.toLocaleString()})
            </div>
          </div>`;
            const popup = new mapboxgl.Popup({ offset: 14, closeButton: false }).setHTML(popupHtml);
            return new mapboxgl.Marker({ element: el })
              .setLngLat(p.center)
              .setPopup(popup)
              .addTo(map);
          },
        });
      }
    }

    const wantedKeys = new Set(wanted.map((w) => w.key));
    for (const [key, marker] of [...live]) {
      if (!wantedKeys.has(key)) { marker.remove(); live.delete(key); }
    }
    for (const w of wanted) {
      if (!live.has(w.key)) live.set(w.key, w.make());
    }
  }, [placePinsSig, placeOwnerKey, routes, selectedClassification, ready]);

  // The map itself can go away without this effect running again (unmount, or
  // a hard rebuild). Markers are DOM nodes we own; drop them with the panel.
  useEffect(() => {
    const live = placeMarkersRef.current;
    return () => { for (const [, m] of live) m.remove(); live.clear(); };
  }, []);

  // Frame the trip the INSTANT its endpoints are known — before any routes
  // finish computing — so opening a faraway trip (e.g. SF→Half Moon Bay while
  // physically in New York) jumps straight to it instead of lingering on the
  // user's location. Runs once per distinct trip; the route-draw effect later
  // refines the framing to the actual polylines.
  const tripFramedRef = useRef<string>("");
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (!start && !end) { tripFramedRef.current = ""; return; }
    const key = `${start?.join(",") ?? ""}|${end?.join(",") ?? ""}`;
    if (tripFramedRef.current === key) return;
    tripFramedRef.current = key;
    // A new trip is a legitimate reason to take the camera back, so the
    // gesture flag starts clean here and only this effect resets it.
    userMovedRef.current = false;
    cameraLog("tripFrame/newTrip", { key });
    if (start && end) {
      const b = bbox([start, end], 0.05);
      map.fitBounds(
        [[b.minLng, b.minLat], [b.maxLng, b.maxLat]],
        { padding: { top: 100, bottom: 200, left: 60, right: 60 }, duration: 0 },
      );
    } else {
      const target = (end ?? start)!;
      map.jumpTo({ center: target, zoom: Math.max(map.getZoom(), 12) });
    }
  }, [start, end, ready]);

  // User-location pulsing dot (shown on the home map before routes load).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    if (!userLocation) {
      userMarkerRef.current?.remove();
      userMarkerRef.current = null;
      return;
    }

    if (!userMarkerRef.current) {
      const el = document.createElement("div");
      el.style.position = "relative";
      el.style.width = "22px";
      el.style.height = "22px";
      el.innerHTML = `
        <span style="position:absolute;inset:-10px;border-radius:9999px;background:rgba(37,99,235,0.18);animation:pleasant-pulse 2s ease-out infinite"></span>
        <span style="position:absolute;inset:0;border-radius:9999px;background:#2563eb;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.25)"></span>
      `;
      userMarkerRef.current = new mapboxgl.Marker({ element: el })
        .setLngLat(userLocation)
        .addTo(map);
    } else {
      userMarkerRef.current.setLngLat(userLocation);
    }

    // Center on user when there are no routes drawn AND the user hasn't set a
    // start OR a destination. Otherwise GPS ticks (~1/s) would yank the camera
    // back to the user's location and override the framing of a freshly-loaded
    // trip (e.g. opening SF→Half Moon Bay while physically in New York).
    if (routes.length === 0 && !start && !end) {
      const targetZoom = Math.max(map.getZoom(), 14);
      const firstFix = !userMarkerRef.current
        || (window.localStorage.getItem("pleasant.lastLocation") == null);
      if (firstFix) {
        cameraLog("userLocation/jumpTo(firstFix)", { targetZoom });
        map.jumpTo({ center: userLocation, zoom: targetZoom });
      } else {
        cameraLog("userLocation/easeTo", { targetZoom });
        map.easeTo({ center: userLocation, zoom: targetZoom, duration: 600 });
      }
    }

    // Persist for next cold start so the map opens at the right place.
    // Skip the write if the rounded location hasn't meaningfully changed —
    // GPS watch updates fire every ~1s and writing localStorage on each
    // tick is wasted main-thread work during navigation.
    try {
      const key = `${userLocation[0].toFixed(3)},${userLocation[1].toFixed(3)}`;
      if (lastSavedLocationRef.current !== key) {
        lastSavedLocationRef.current = key;
        window.localStorage.setItem(
          "pleasant.lastLocation",
          JSON.stringify({ c: userLocation, z: 14 }),
        );
      }
    } catch { /* Remembering the last location is a convenience. A full or
                 unwritable quota is not worth an error here. */ }
  }, [userLocation, ready, routes.length]);

  // Transient highlight overlay. Subscribes to `requestMapHighlight()` from
  // anywhere in the app (e.g. ScoreBreakdown's "View on map" buttons) and
  // draws an amber polyline + pulsing POI markers, auto-clearing on a TTL.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const clearHighlight = () => {
      // Guard: map may already be removed during unmount cleanup, in which
      // case `map.style` is undefined and getLayer() throws.
      try {
        if (!map || !map.style || (map as unknown as { _removed?: boolean })._removed) {
          // still clean up DOM markers below
        } else {
          if (map.getLayer("route-highlight")) map.removeLayer("route-highlight");
          if (map.getLayer("route-highlight-glow")) map.removeLayer("route-highlight-glow");
          if (map.getSource("route-highlight")) map.removeSource("route-highlight");
        }
      } catch { /* map disposed mid-cleanup — ignore */ }
      highlightMarkersRef.current.forEach((m) => m.remove());
      highlightMarkersRef.current = [];
      if (highlightLabelRef.current) {
        highlightLabelRef.current.remove();
        highlightLabelRef.current = null;
      }
      if (highlightTimerRef.current != null) {
        window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    };

    const apply = (payload: MapHighlightPayload | null) => {
      clearHighlight();
      if (!payload) return;
      if (!map.isStyleLoaded()) {
        map.once("styledata", () => apply(payload));
        return;
      }
      const allPts: LngLat[] = [];

      if (payload.coords && payload.coords.length >= 2) {
        map.addSource("route-highlight", {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: { type: "LineString", coordinates: payload.coords },
            properties: {},
          },
        });
        map.addLayer({
          id: "route-highlight-glow",
          type: "line",
          source: "route-highlight",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": "#fbbf24", "line-width": 18, "line-opacity": 0.35, "line-blur": 6 },
        });
        map.addLayer({
          id: "route-highlight",
          type: "line",
          source: "route-highlight",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": "#f59e0b", "line-width": 7, "line-opacity": 0.95 },
        });
        allPts.push(...payload.coords);
      }

      for (const poi of payload.pois ?? []) {
        const el = document.createElement("div");
        el.style.cssText = "position:relative;width:18px;height:18px;pointer-events:none;";
        el.innerHTML = `
          <span style="position:absolute;inset:-10px;border-radius:9999px;background:rgba(245,158,11,0.30);animation:pleasant-pulse 1.6s ease-out infinite"></span>
          <span style="position:absolute;inset:0;border-radius:9999px;background:#f59e0b;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></span>
        `;
        if (poi.name) el.title = poi.name;
        const m = new mapboxgl.Marker({ element: el }).setLngLat(poi.point).addTo(map);
        highlightMarkersRef.current.push(m);
        allPts.push(poi.point);
      }

      if (payload.fit !== false && allPts.length) {
        cameraLog("highlight/fitBounds", { points: allPts.length });
        const b = bbox(allPts, 0.005);
        map.fitBounds(
          [[b.minLng, b.minLat], [b.maxLng, b.maxLat]],
          { padding: { top: 100, bottom: 260, left: 60, right: 60 }, duration: 700, maxZoom: 15 },
        );
      }

      if (payload.label) {
        const label = document.createElement("div");
        label.style.cssText = `
          position:absolute;top:14px;left:50%;transform:translateX(-50%);
          background:rgba(245,158,11,0.95);color:white;
          padding:6px 12px;border-radius:9999px;
          font-size:12px;font-weight:600;letter-spacing:0.01em;
          box-shadow:0 4px 12px rgba(0,0,0,0.15);
          pointer-events:none;z-index:5;white-space:nowrap;max-width:80vw;
          overflow:hidden;text-overflow:ellipsis;
        `;
        label.textContent = payload.label;
        map.getContainer().appendChild(label);
        highlightLabelRef.current = label;
      }

      const ttl = payload.ttlMs ?? 4500;
      highlightTimerRef.current = window.setTimeout(clearHighlight, ttl);
    };

    const unsub = subscribeMapHighlight(apply);
    return () => { unsub(); clearHighlight(); };
  }, [ready]);

  // Heading-up camera control. When `headingUp` is on, smoothly rotate the
  // map to the user's current heading and (for driving) tilt the camera so
  // the road appears in 3D. When toggled off, snap back to north-up flat.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    if (!headingUp) {
      cameraLog("heading/resetNorthUp");
      map.easeTo({ bearing: 0, pitch: 0, duration: 500 });
      return;
    }

    const pitch = travelMode === "driving" ? 55 : 0;
    const target: Parameters<mapboxgl.Map["easeTo"]>[0] = { bearing: heading ?? 0, pitch, duration: 400 };
    if (userLocation) {
      target.center = userLocation;
      // Pull in a bit closer so the perspective reads as "you are here".
      target.zoom = Math.max(map.getZoom(), travelMode === "driving" ? 16 : 17);
    }
    // THE COMPASS PATH. `heading` updates up to 10Hz on any >=2 degree
    // change, so with heading-up ON this recentres on the user and ratchets
    // the zoom on every tick. If the user's report is this path, these lines
    // will flood the console during the gesture and the answer is settled
    // without anyone having to remember whether the button was lit.
    cameraLog("heading/follow", {
      headingDeg: Math.round(heading ?? 0),
      recentresOnUser: !!userLocation,
      ratchetFloor: userLocation ? (travelMode === "driving" ? 16 : 17) : "none",
    });
    map.easeTo(target);
  }, [heading, headingUp, travelMode, userLocation, ready]);

  // Long-press to drop a photo pin at a specific spot. We listen on both
  // touchstart (mobile) and mousedown (desktop) and trigger after ~550ms
  // unless the pointer moves. Movement / map drag cancels the gesture.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !onLongPress) return;
    const canvas = map.getCanvas();
    let timer: number | null = null;
    let startPx: { x: number; y: number } | null = null;
    const cancel = () => {
      if (timer != null) { window.clearTimeout(timer); timer = null; }
      startPx = null;
    };
    const startAt = (x: number, y: number) => {
      startPx = { x, y };
      timer = window.setTimeout(() => {
        if (!startPx) return;
        const ll = map.unproject([startPx.x, startPx.y]);
        onLongPressRef.current?.([ll.lng, ll.lat]);
        cancel();
      }, 550);
    };
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      startAt(e.offsetX, e.offsetY);
    };
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const rect = canvas.getBoundingClientRect();
      startAt(e.touches[0].clientX - rect.left, e.touches[0].clientY - rect.top);
    };
    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", cancel);
    canvas.addEventListener("mouseup", cancel);
    canvas.addEventListener("mouseleave", cancel);
    canvas.addEventListener("touchstart", onTouchStart, { passive: true });
    canvas.addEventListener("touchmove", cancel, { passive: true });
    canvas.addEventListener("touchend", cancel);
    canvas.addEventListener("touchcancel", cancel);
    map.on("dragstart", cancel);
    map.on("zoomstart", cancel);
    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", cancel);
      canvas.removeEventListener("mouseup", cancel);
      canvas.removeEventListener("mouseleave", cancel);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", cancel);
      canvas.removeEventListener("touchend", cancel);
      canvas.removeEventListener("touchcancel", cancel);
      map.off("dragstart", cancel);
      map.off("zoomstart", cancel);
      cancel();
    };
  }, [ready, onLongPress]);

  // Render photo pins as circular thumbnail markers.
  // Diff against the previous set so adding/removing a single pin doesn't
  // tear down and recreate every marker (which thrashes the DOM and the
  // map layer when there are 100s of pins).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const next = new Set<string>();
    for (const pin of photoPins) {
      next.add(pin.id);
      // Update existing marker position (cheap) and skip recreation.
      const existing = photoMarkersRef.current.get(pin.id);
      if (existing) {
        existing.setLngLat([pin.lng, pin.lat]);
        continue;
      }
      const el = document.createElement("button");
      el.type = "button";
      el.setAttribute("aria-label", pin.caption ? `Photo: ${pin.caption}` : "Photo pin");
      el.style.cssText =
        "all: unset; cursor: pointer; width: 36px; height: 36px; border-radius: 9999px;" +
        "border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.35);" +
        "background-size: cover; background-position: center;" +
        "transition: transform 120ms;" +
        (pin.photoUrl ? `background-image: url('${pin.photoUrl.replace(/'/g, "%27")}');` : "background: hsl(var(--muted));");
      if (!pin.is_public) el.style.outline = "2px solid hsl(var(--primary))";
      el.addEventListener("mouseenter", () => { el.style.transform = "scale(1.1)"; });
      el.addEventListener("mouseleave", () => { el.style.transform = "scale(1)"; });
      el.addEventListener("click", (ev) => {
        ev.stopPropagation();
        onPhotoPinClickRef.current?.(pin);
      });
      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([pin.lng, pin.lat])
        .addTo(map);
      photoMarkersRef.current.set(pin.id, marker);
    }
    // Remove markers no longer present.
    for (const [id, marker] of photoMarkersRef.current) {
      if (!next.has(id)) {
        marker.remove();
        photoMarkersRef.current.delete(id);
      }
    }
  }, [photoPins, ready]);

  // Render standalone tip pins as small colored badges with a category icon.
  // Same diff strategy as photo pins.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const GLYPHS: Record<TipPin["category"], string> = {
      tip:            '<path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2V17h6v-.3c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z"/>',
      warning:        '<path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
      recommendation: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
      info:           '<circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>',
    };
    const COLORS_TIP: Record<TipPin["category"], string> = {
      tip: "hsl(40 95% 50%)",
      warning: "hsl(0 75% 55%)",
      recommendation: "hsl(155 60% 40%)",
      info: "hsl(200 75% 50%)",
    };

    const next = new Set<string>();
    for (const tip of tipPins) {
      next.add(tip.id);
      const existing = tipMarkersRef.current.get(tip.id);
      if (existing) {
        existing.setLngLat([tip.lng, tip.lat]);
        continue;
      }
      const el = document.createElement("button");
      el.type = "button";
      el.setAttribute("aria-label", `${tip.category} tip`);
      el.style.cssText =
        "all: unset; cursor: pointer; width: 28px; height: 28px; border-radius: 9999px;" +
        "border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);" +
        "display: flex; align-items: center; justify-content: center; color: white;" +
        "transition: transform 120ms;" +
        `background: ${COLORS_TIP[tip.category]};`;
      el.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">${GLYPHS[tip.category]}</svg>`;
      el.addEventListener("mouseenter", () => { el.style.transform = "scale(1.15)"; });
      el.addEventListener("mouseleave", () => { el.style.transform = "scale(1)"; });
      el.addEventListener("click", (ev) => {
        ev.stopPropagation();
        onTipPinClickRef.current?.(tip);
      });
      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([tip.lng, tip.lat])
        .addTo(map);
      tipMarkersRef.current.set(tip.id, marker);
    }
    for (const [id, marker] of tipMarkersRef.current) {
      if (!next.has(id)) {
        marker.remove();
        tipMarkersRef.current.delete(id);
      }
    }
  }, [tipPins, ready]);

  // ZOOM-GATE community pins (Morgan: "I don't want these pictures always on
  // the map"). Tip lightbulbs and photo pins are neighborhood-scale content —
  // at state/country zoom they read as random clutter stuck to the screen.
  // Show them only when the camera is close enough for them to mean something.
  const COMMUNITY_PIN_MIN_ZOOM = 11;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const applyPinVisibility = () => {
      const visible = map.getZoom() >= COMMUNITY_PIN_MIN_ZOOM;
      const display = visible ? "" : "none";
      for (const [, m] of tipMarkersRef.current) m.getElement().style.display = display;
      for (const [, m] of photoMarkersRef.current) m.getElement().style.display = display;
    };
    applyPinVisibility();
    map.on("zoomend", applyPinVisibility);
    return () => { map.off("zoomend", applyPinVisibility); };
  }, [ready, tipPins, photoPins]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className={`flex items-center justify-center bg-muted text-muted-foreground ${className ?? ""}`}>
        Mapbox token missing
      </div>
    );
  }

  return (
    // The outer wrapper must be a positioned element with intrinsic size so
    // the absolutely-positioned canvas container below has something to size
    // against. Callers commonly pass `className="absolute inset-0"`, but if
    // they also pass plain layout classes the cascade can leave the wrapper
    // with `position: relative` and `inset: 0` (which only OFFSETS, doesn't
    // size). Forcing `w-full h-full` guarantees a real box for Mapbox.
    <div className={`relative w-full h-full ${className ?? ""}`}>
      <div ref={containerRef} className="absolute inset-0" />
      <div className="pleasant-mapmark">Pleasant</div>

      {/* Bike-lanes overlay REMOVED (Morgan: "we don't need that at all,
       *  it's too confusing") — the infrastructure scribble read as broken
       *  routes. The layer plumbing (showBikeLanes state + effects) stays
       *  dormant with the toggle gone; delete fully in a cleanup pass. */}

      {onToggleHeadingUp && (
        <button
          type="button"
          onClick={onToggleHeadingUp}
          aria-label={headingUp ? "Switch to north-up" : "Rotate map to my direction"}
          aria-pressed={headingUp}
          className="absolute bottom-safe-28 right-3 md:right-5 z-10 flex flex-col items-center gap-1 group"
        >
          <span
            className={`w-11 h-11 rounded-full border shadow-elevated flex items-center justify-center transition-smooth ${
              headingUp
                ? "bg-pleasant text-primary-foreground border-transparent"
                : "bg-card text-foreground border-border group-hover:border-primary/40"
            }`}
          >
            {/* Compass needle. When heading-up is on, the needle stays still
             *  (since the map itself rotates). When off, the needle rotates
             *  to indicate the user's current heading relative to north. */}
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: `rotate(${headingUp ? 0 : -(heading ?? 0)}deg)`,
                transition: "transform 200ms",
              }}
            >
              <polygon points="12 2 15 12 12 22 9 12 12 2" fill="currentColor" stroke="none" />
              <line x1="12" y1="2" x2="12" y2="12" stroke="hsl(var(--background))" strokeWidth="1.5" />
            </svg>
          </span>
        </button>
      )}
      {/* (#194) "Show/Hide Direct route" overlay button removed. Under
       *  three-cards-always (#190), Direct is one of the surfaced cards
       *  and its polyline is rendered with the others — the overlay
       *  toggle is redundant. The showGoogleRoute prop is kept on the
       *  component signature in case the parent still wires it (no-op
       *  here), but the floating button is gone. */}
      {/* Google stats label was removed (#79) — kept colliding with header
       *  pills and the Drive/Walk/Bike toggle no matter where we placed
       *  it. The route card itself already conveys the Pleasant vs Google
       *  comparison (with distance delta), so the floating chip was
       *  redundant. The diagnostic panel below is now bound to a long-
       *  press on the "Hide Google route" button when we need it again. */}
      {/* Diagnostic dialog (#74). Tells you whether Google's polyline
       *  ACTUALLY differs from the selected Pleasant route's polyline.
       *  Jaccard overlap ≥ 0.8 means the two paths share most of their
       *  geometry — the lines really are the same, and the duration
       *  difference would be an engine disagreement, not a path
       *  difference. < 0.5 means they're substantially different paths.
       *  Currently dormant — diagOpen is never set true now that the chip
       *  is gone. Will re-attach to a long-press if we need it again. */}
      {/* eslint-disable-next-line no-constant-binary-expression -- the `false`
          is the kill switch described above, not a dead guard. Flip it to
          re-arm the diagnostic; deleting the branch would lose the panel. */}
      {false && diagOpen && showGoogleRoute && (
        <div
          className="absolute top-[14.5rem] md:top-[12.5rem] right-3 md:right-5 z-20 w-[18rem] max-w-[calc(100vw-1.5rem)] rounded-2xl border border-border bg-card shadow-elevated p-3 text-[12px] text-foreground/90"
        >
          {(() => {
            const googleCoords = googleRouteCoords ?? [];
            const selectedCoords = selectedRouteCoords ?? [];
            const overlap =
              googleCoords.length >= 2 && selectedCoords.length >= 2
                ? jaccard(geometryBuckets(googleCoords), geometryBuckets(selectedCoords))
                : null;
            // Self-overlap detection (#75): a Mapbox walking route that
            // doubles back on the same streets produces a high self-
            // overlap. Visible footprint matches Google's, but the
            // underlying geometry is ~2× as long — explains why the
            // distances disagree.
            const selectedSelfOverlap =
              selectedCoords.length >= 4 ? selfOverlapFraction(selectedCoords) : 0;
            const googleSelfOverlap =
              googleCoords.length >= 4 ? selfOverlapFraction(googleCoords) : 0;
            const verdict =
              overlap == null
                ? "Can't compare — one polyline missing."
                : selectedSelfOverlap >= 0.25
                  ? "Pleasant route doubles back on itself — couldn't generate a second route."
                  : overlap >= 0.8
                    ? "Lines are essentially the same path."
                    : overlap >= 0.5
                      ? "Paths overlap a lot but differ in places."
                      : "Paths are substantially different.";
            const fmtCoord = (c?: [number, number]): string =>
              c ? `${c[0].toFixed(4)}, ${c[1].toFixed(4)}` : "—";
            return (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">Path comparison</div>
                  <button
                    type="button"
                    onClick={() => setDiagOpen(false)}
                    aria-label="Close diagnostic panel"
                    className="text-foreground/60 hover:text-foreground text-base leading-none px-1"
                  >
                    ×
                  </button>
                </div>
                <div className="rounded-md bg-pleasant/10 border border-primary/30 px-2 py-1.5 text-[11px] text-foreground/85">
                  {verdict}
                  {overlap != null && (
                    <span className="block text-foreground/60 tabular-nums">
                      Cross-route overlap: {Math.round(overlap * 100)}%
                    </span>
                  )}
                  <span className="block text-foreground/60 tabular-nums">
                    Pleasant self-overlap: {Math.round(selectedSelfOverlap * 100)}%
                    {selectedSelfOverlap >= 0.25 ? " ⚠" : ""}
                  </span>
                  <span className="block text-foreground/60 tabular-nums">
                    Google self-overlap: {Math.round(googleSelfOverlap * 100)}%
                  </span>
                </div>
                <div className="space-y-1.5">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {selectedRouteLabel ?? "Selected route"} (Pleasant Routes)
                    </div>
                    <div className="tabular-nums">
                      {typeof selectedRouteDurationS === "number"
                        ? `${Math.round(selectedRouteDurationS / 60)} min`
                        : "—"}
                      {" · "}
                      {typeof selectedRouteDistanceM === "number"
                        ? fmtDistance(selectedRouteDistanceM)
                        : "—"}
                      {" · "}
                      {selectedCoords.length} coords
                    </div>
                    <div className="text-[10px] text-foreground/55 tabular-nums">
                      start {fmtCoord(selectedCoords[0])}
                      <br />
                      end &nbsp;&nbsp;{fmtCoord(selectedCoords[selectedCoords.length - 1])}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground" style={{ color: "#1a56db" }}>
                      Reference route
                    </div>
                    <div className="tabular-nums">
                      {typeof googleDurationS === "number"
                        ? `${Math.round(googleDurationS / 60)} min`
                        : "—"}
                      {" · "}
                      {typeof googleDistanceM === "number"
                        ? fmtDistance(googleDistanceM)
                        : "—"}
                      {" · "}
                      {googleCoords.length} coords
                    </div>
                    <div className="text-[10px] text-foreground/55 tabular-nums">
                      start {fmtCoord(googleCoords[0])}
                      <br />
                      end &nbsp;&nbsp;{fmtCoord(googleCoords[googleCoords.length - 1])}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-foreground/45 pt-1 border-t border-border">
                  Tap the chip again to close.
                </div>
              </div>
            );
          })()}
        </div>
      )}
      {onAddPhotoHere && (
        <button
          type="button"
          onClick={onAddPhotoHere}
          aria-label="Take a photo and pin it here"
          className="absolute bottom-safe-44 right-3 md:right-5 z-10 flex flex-col items-center gap-1 group"
        >
          <span className="w-11 h-11 rounded-full border bg-card text-foreground border-border shadow-elevated flex items-center justify-center group-hover:border-primary/40 transition-smooth">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
              <circle cx="12" cy="13" r="3" />
            </svg>
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-card/90 text-foreground shadow-soft border border-border">
            Photo
          </span>
        </button>
      )}
    </div>
  );
};


/** Escape HTML for safe injection into Mapbox popup HTML strings. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
